import { useEffect, useState } from "react";
import {
  DyteAudioVisualizer,
  DyteAvatar,
  DyteCameraToggle,
  DyteClock,
  DyteDialogManager,
  DyteLeaveButton,
  DyteMicToggle,
  DyteNameTag,
  DyteParticipantCount,
  DyteParticipantsAudio,
  DyteParticipantTile,
} from "@dytesdk/react-ui-kit";
import { provideDyteDesignSystem, UIConfig } from "@dytesdk/ui-kit";
import type DyteClient from "@dytesdk/web-core";
import { DyteParticipant } from "@dytesdk/web-core";
import { useNavigate } from "react-router-dom";

import { CustomStates, SetStates } from "../../../../../types/dyte";

function InMeeting({
  meeting,
  config,
  states,
  setStates,
}: {
  meeting: DyteClient;
  config: UIConfig;
  states: CustomStates;
  setStates: SetStates;
  sessionId: string;
}) {
  const navigate = useNavigate();
  const localUser = meeting.self;

  const [remoteParticipant, setRemoteParticipant] =
    useState<DyteParticipant | null>(null);

  useEffect(() => {
    const activeParticipants = meeting.participants.active.toArray();
    const initialRemoteParticipant = activeParticipants[0];
    if (initialRemoteParticipant) {
      setRemoteParticipant(initialRemoteParticipant);
    }

    const handleParticipantJoined = (participant: DyteParticipant) => {
      setRemoteParticipant(participant);
    };

    const handleParticipantLeft = () => {
      setRemoteParticipant(null);
    };

    meeting.participants.joined.on(
      "participantJoined",
      handleParticipantJoined
    );
    meeting.participants.active.on("participantLeft", handleParticipantLeft);

    return () => {
      meeting.participants.active.removeListener(
        "participantJoined",
        handleParticipantJoined
      );
      meeting.participants.active.removeListener(
        "participantLeft",
        handleParticipantLeft
      );

      meeting.leaveRoom();
    };
  }, [meeting]);

  return (
    <div className="h-[calc(100vh-64px)]">
      <div
        className="flex h-full flex-col gap-4"
        ref={(el) => {
          if (!el) return;

          provideDyteDesignSystem(el, {
            theme: "light",
          });

          el?.addEventListener("dyteStateUpdate", (e) => {
            const { detail: newStateUpdate } = e as unknown as {
              detail: CustomStates;
            };
            setStates((oldState: CustomStates) => {
              return {
                ...oldState,
                ...newStateUpdate,
              };
            });
          });
        }}
      >
        <div className="relative flex items-center justify-center">
          <div className="absolute right-0 top-0 flex items-center gap-2">
            <DyteParticipantCount
              meeting={meeting}
              className="h-auto text-vincer-900"
            />
            <DyteClock meeting={meeting} className="h-auto text-vincer-900" />
          </div>

          <div className="flex flex-col items-center justify-center rounded-xl bg-vincer-700/90">
            <DyteParticipantTile
              participant={localUser}
              meeting={meeting}
              style={{
                borderRadius: "12px",
                width: "213px",
                height: "120px",
                aspectRatio: "16/9",
                backgroundColor: "transparent",
              }}
            >
              <DyteNameTag participant={localUser} meeting={meeting}>
                <DyteAudioVisualizer participant={localUser} />
              </DyteNameTag>
              <DyteAvatar size="sm" participant={localUser} />
            </DyteParticipantTile>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <div className="flex h-full items-center justify-start">
            {remoteParticipant ? (
              <div className="size-full rounded-xl border border-vincer-100 bg-vincer-100/50">
                <DyteParticipantTile
                  participant={remoteParticipant}
                  meeting={meeting}
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "transparent",
                    borderRadius: "12px",
                  }}
                >
                  <DyteNameTag
                    participant={remoteParticipant}
                    meeting={meeting}
                  >
                    <DyteAudioVisualizer participant={remoteParticipant} />
                  </DyteNameTag>
                  <DyteAvatar size="sm" participant={remoteParticipant} />
                </DyteParticipantTile>
              </div>
            ) : (
              <div className="flex size-full flex-col items-center justify-center rounded-xl bg-vincer-100/50">
                <p className="font-medium text-vincer-900">
                  Mentor is joining...
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center space-x-2">
          <DyteMicToggle size="md" meeting={meeting} variant="horizontal" />
          <DyteCameraToggle size="md" meeting={meeting} variant="horizontal" />
          <DyteLeaveButton
            size="md"
            variant="horizontal"
            onClick={() => {
              meeting.leave();
              navigate(`/mentee`);
            }}
          />
        </div>
      </div>
      <div className="absolute bottom-0 left-0">
        <div className="container mx-auto p-4">
          <DyteParticipantsAudio meeting={meeting} />
          <DyteDialogManager
            meeting={meeting}
            config={config}
            states={states}
          />
        </div>
      </div>
    </div>
  );
}

export default InMeeting;
